import { create } from "zustand";
import {
  AdminGetRoutesResDto,
  RouteStartingPointDto,
} from "service/api/estabrookSchemas";
import {
  DriverMarker,
  MapMarker,
  LassoMode,
} from "../components/map/map.types";
import {
  getMinAndMaxTime,
  RoutesMinAndMaxStartTime,
} from "../pages/dashboard/route-planner/helpers/timetable";
import { hasInProgressLocations } from "../utils/route-planner";
import {
  PhaseStatus,
  TransitionPhase,
} from "../pages/dashboard/route-planner/transition-routes-modal";
import { DateTime } from "luxon";

export enum AddressStatus {
  Skipped = "skipped",
  Regular = "regular",
  Delayed = "delayed",
  Urgent = "urgent",
}
export type LocationAddressesDto = {
  id: string;
  latitude: number;
  longitude: number;
  locationSequence: number;
  status?: AddressStatus;
  selected?: boolean;
};

export type MoveValues = {
  route: string;
  sequence: number;
  order: string;
  status: AddressStatus;
};

export type MoveOrders = {
  targetRouteId: string;
  targetRouteName: string;
  targetSeqNumber: number;
  targetRouteOrderStatus: "skipped" | "regular" | "delayed" | "urgent";
  transferMode: "before" | "after";
  locationIds: MoveOrdersLocationIds[];
  visibleTargetSequence: number;
};

export type MoveOrdersLocationIds = {
  locationId: string;
  seqNumber: number;
};

export type PendingDriverUpdateValues = {
  driverId: string;
  driverName: string;
  routeId: string;
  routeName: string;
  assignedRoutes: string[];
  startTime: string;
} | null;

export type PendingNotificationUpdateValues = {
  routeId: string;
  routeName: string;
} | null;

export type MoveDialog = {
  open: boolean;
  step: number;
};

export type TransitioningRoutePhase = {
  status: PhaseStatus;
  phase: TransitionPhase;
};

export enum Modals {
  REVERSE = "reverseModal",
  CREATE_VIRTUAL_ROUTE = "createVirtualRoute",
  DELETE_VIRTUAL_ROUTE = "deleteVirtualRoute",
  END_SHIFT = "endShiftModal",
  BULK_EDIT_TIME = "bulkEditTime",
  EDIT_ROUTE_DRIVER = "editRouteDriver",
  CONFIRM_RESET = "confirmReset",
  CONFIRM_UPDATE_DRIVERS = "confirmUpdateDrivers",
  DISPATCH_DISABLED = "dispatchDisabled",
  CREATE_ROUTES_DISABLED = "createRoutesDisabled",
  CANNOT_UPDATE_ROUTE = "cannotUpdateRoute",
  REVERT_TO_PLANNING = "revertToPlanning",
  MUTE_ALL_NOTIFICATIONS_ROUTE = "muteAllNotificationsRoute",
  SEND_ALL_NOTIFICATIONS_ROUTE = "sendAllNotificationsRoute",
  MUTE_ALL_NOTIFICATIONS_GLOBAL = "muteAllNotificationsGlobal",
  SEND_ALL_NOTIFICATIONS_GLOBAL = "sendAllNotificationsGlobal",
  CUSTOMIZE_NOTIFICATIONS = "customizeNotifications",
  CUSTOMIZE_GLOBAL_NOTIFICATIONS = "customizeGlobalNotifications",
  ADD_CUSTOMER = "addCustomer",
  CONFIRM_MOVE_UNASSIGNED_TO_ROUTE = "confirmMoveUnassignedToRoute",
}

export type PendingVirtualRouteDeleteValues = {
  routeId: string;
  routeName: string;
};

export type PendingUnassignedMoveValues = {
  moveOrders: MoveOrders;
  selectedDate: string;
  mutationFn: (params: any) => Promise<any>;
  onSuccess?: () => void;
};

export type RoutePlannerState = {
  routes: AdminGetRoutesResDto[];
  addresses: MapMarker[];
  date: string;
  query: string;
  sidebar: boolean;
  drawing: boolean;
  lassoMode: LassoMode;
  routeLocked: boolean;
  virtualRoute: boolean;
  mapZoom: number;
  mapCentering: MapMarker[];
  driverMarkers: DriverMarker[];
  driverMapCentering: RouteStartingPointDto | undefined;
  mapHeight: string;
  selectedAddresses: MapMarker[];
  selectedAddressesForSequencing: MapMarker[];
  sequencedAddresses: MapMarker[];
  remainingSequences: number;
  moveDialogType: "moveToRoute" | "changeStatus" | "";
  moveDialog: MoveDialog;
  moveValues: MoveValues;
  moveOrders: MoveOrders;
  moveOrdersDialogOpen: boolean;
  pendingDriverUpdate?: PendingDriverUpdateValues;
  pendingNotificationUpdate?: PendingNotificationUpdateValues;
  pendingVirtualRouteDelete?: PendingVirtualRouteDeleteValues;
  pendingUnassignedMove?: PendingUnassignedMoveValues;
  newVirtualRoutePriority: number;
  allSequenced: boolean;
  validSequence: boolean;
  sameRegionId: boolean;
  sameShift: boolean;
  minAndMaxStartTimeOfRoutes: RoutesMinAndMaxStartTime;
  isLoadingModalShown: boolean;
  lastSequence?: number;
  shouldExcludeLocation: boolean;
  transitioningRoutesPhase?: TransitioningRoutePhase;
  activeModal: Modals | null;
  hasLiveStateChanges: boolean;
  hasPlanningChanges: boolean;
  searchedAddressForCustomer: string;
  isCalculatingEstTime: boolean;
  isCommittingLiveRoutes: boolean;
  initialLiveEstSettlementDate?: string;
  initialLiveEstSettlementClearedAt?: number;
  // While set, transient backend "hasChanges" reports for this date are ignored
  // (admin-triggered status updates settle automatically in the background)
  driverStatusSettlementDate?: string;
  driverStatusSettlementStartedAt?: number;
  driverStatusSettlementClearedAt?: number;
  // Track routes that have pending EST time calculations (between status update and EST_TIME_CALCULATED events)
  pendingEstCalculationRouteIds: Set<string>;
  // Centralized synchronized current time - all components should read from this
  // to ensure they update at exactly the same moment
  syncedCurrentTime: DateTime;
};

export type RoutePlannerActions = {
  setMapHeight: (val: string) => void;
  setRoutes: (routes: AdminGetRoutesResDto[]) => void;
  setAddresses: (address: MapMarker[]) => void;
  setSidebar: (val: boolean) => void;
  toggleDrawing: (val: boolean, mode?: LassoMode) => void;
  setRouteLocked: (val: boolean) => void;
  setVirtualRoute: (val: boolean) => void;
  findAddress: (id: string) => MapMarker | undefined;
  setMapCentering: (id: string | undefined, addresses?: MapMarker[]) => void;
  clearMapCentering: () => void;
  setDriverMarkers: (driverMarkers: DriverMarker[]) => void;
  updateDriverMarkerLiveLocation: (
    driverId: string | null | undefined,
    liveLocation: DriverMarker["liveLocation"] | undefined,
  ) => void;
  setDriverMapCentering: (startingPoint?: RouteStartingPointDto) => void;
  setMapZoom: (val: number) => void;
  setDate: (val: string) => void;
  setQuery: (val: string) => void;
  removeSelected: (id: string) => void;
  resetAllSelected: () => void;
  setDirectSelection: (addresses: MapMarker[]) => void;
  setSelectedAddress: (val: MapMarker[], marker?: boolean) => void;
  updateSelectedAddresses: (selectedAddresses: MapMarker[]) => void;
  updateAddressStatus: (locationId: string, newStatus: AddressStatus) => void;
  setMoveDialog: (moveDialogValues: MoveDialog) => void;
  setMoveValues: (values: MoveValues) => void;
  setMoveOrders: (orders: MoveOrders) => void;
  setMoveOrdersDialogOpen: (val: boolean) => void;
  setMoveDialogType: (val: "moveToRoute" | "changeStatus" | "") => void;
  setAllSequenced: (val: boolean) => void;
  setPendingDriverUpdate: (values: PendingDriverUpdateValues) => void;
  setPendingNotificationUpdate: (
    values: PendingNotificationUpdateValues,
  ) => void;
  setPendingVirtualRouteDelete: (val: PendingVirtualRouteDeleteValues) => void;
  setPendingUnassignedMove: (val?: PendingUnassignedMoveValues) => void;
  setNewVirtualRoutePriority: (val: number) => void;
  setIsLoadingModalShown: (show: boolean) => void;
  setSelectedAddressesForSequencing: (
    values: MapMarker[],
    lastSequence?: number,
  ) => void;
  setAddressSequence: (
    addressId: string,
    lastSequence?: number,
    sequencingUnassigned?: boolean,
  ) => void;
  setSequencedAddresses: (addresses: MapMarker[]) => void;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  setTransitioningRoutesPhase: (
    transitioningRoutesPhase?: TransitioningRoutePhase,
  ) => void;
  setActiveModal: (activeModal: Modals | null) => void;
  setHasLiveStateChanges: (hasLiveStateChanges: boolean) => void;
  setHasPlanningChanges: (hasPlanningChanges: boolean) => void;
  setSearchedAddressForCustomer: (address: string) => void;
  setIsCalculatingEstTime: (isCalculatingEstTime: boolean) => void;
  setIsCommittingLiveRoutes: (isCommittingLiveRoutes: boolean) => void;
  setInitialLiveEstSettlementDate: (date?: string) => void;
  setDriverStatusSettlementDate: (date?: string) => void;
  openAddCustomerModal: (searchedAddress: string) => void;
  closeAddCustomerModal: () => void;
  addPendingEstCalculation: (routeId: string) => void;
  removePendingEstCalculation: (routeId: string) => void;
  clearPendingEstCalculations: () => void;
  // Update the synchronized current time - called by the time sync hook
  setSyncedCurrentTime: (time: DateTime) => void;
};

export type RoutePlannerStore = RoutePlannerState & RoutePlannerActions;

const initialState: RoutePlannerState = {
  sidebar: false,
  drawing: false,
  lassoMode: LassoMode.REPLACE,
  routeLocked: false,
  virtualRoute: false,
  date: "",
  query: "",
  addresses: [] as MapMarker[],
  routes: [] as AdminGetRoutesResDto[],
  selectedAddresses: [] as MapMarker[],
  selectedAddressesForSequencing: [] as MapMarker[],
  sequencedAddresses: [] as MapMarker[],
  remainingSequences: 0,
  mapCentering: [] as MapMarker[],
  driverMarkers: [] as DriverMarker[],
  driverMapCentering: undefined,
  mapZoom: 13,
  mapHeight: "350px",
  minAndMaxStartTimeOfRoutes: {
    min: null,
    max: null,
  },
  moveValues: {
    route: "",
    sequence: 0,
    order: "",
    status: AddressStatus.Regular,
  },
  moveDialog: {
    open: false,
    step: -1,
  },
  moveDialogType: "",
  moveOrders: {
    targetRouteId: "",
    targetRouteName: "",
    targetSeqNumber: 0,
    targetRouteOrderStatus: "regular",
    transferMode: "after",
    locationIds: [],
    visibleTargetSequence: 0,
  },
  moveOrdersDialogOpen: false,
  pendingDriverUpdate: null,
  pendingNotificationUpdate: null,
  pendingVirtualRouteDelete: undefined,
  pendingUnassignedMove: undefined,
  newVirtualRoutePriority: 0,
  allSequenced: false,
  shouldExcludeLocation: false,
  validSequence: false,
  sameRegionId: false,
  sameShift: false,
  isLoadingModalShown: false,
  transitioningRoutesPhase: undefined,
  activeModal: null,
  hasLiveStateChanges: false,
  hasPlanningChanges: false,
  searchedAddressForCustomer: "",
  isCalculatingEstTime: false,
  isCommittingLiveRoutes: false,
  initialLiveEstSettlementDate: undefined,
  initialLiveEstSettlementClearedAt: undefined,
  driverStatusSettlementDate: undefined,
  driverStatusSettlementStartedAt: undefined,
  driverStatusSettlementClearedAt: undefined,
  pendingEstCalculationRouteIds: new Set<string>(),
  syncedCurrentTime: DateTime.now(),
};

const areNestedValuesEqual = (prev: unknown, next: unknown) => {
  if (prev === next) return true;
  if (!prev || !next) return prev === next;

  try {
    return JSON.stringify(prev) === JSON.stringify(next);
  } catch {
    return false;
  }
};

const areMapMarkersEqual = (prev: MapMarker, next: MapMarker) =>
  prev.id === next.id &&
  prev.latitude === next.latitude &&
  prev.longitude === next.longitude &&
  prev.address === next.address &&
  prev.color === next.color &&
  prev.selected === next.selected &&
  prev.duration === next.duration &&
  prev.sequence === next.sequence &&
  prev.locationSequence === next.locationSequence &&
  prev.status === next.status &&
  prev.customerType === next.customerType &&
  prev.routeId === next.routeId &&
  prev.routeName === next.routeName &&
  prev.driverName === next.driverName &&
  prev.driverStatus === next.driverStatus &&
  prev.driverStatusLastUpdatedAt === next.driverStatusLastUpdatedAt &&
  prev.driverStatusChangeReason === next.driverStatusChangeReason &&
  prev.driverStatusChangeReasonComment ===
    next.driverStatusChangeReasonComment &&
  prev.locationNoteCount === next.locationNoteCount &&
  prev.serviceAccess === next.serviceAccess &&
  prev.isFlatRate === next.isFlatRate &&
  prev.isOnDemand === next.isOnDemand &&
  prev.isSnowReporter === next.isSnowReporter &&
  prev.routeStartTime === next.routeStartTime &&
  prev.lastPlowedAt === next.lastPlowedAt &&
  areNestedValuesEqual(prev.snowAmount, next.snowAmount) &&
  areNestedValuesEqual(prev.customer, next.customer) &&
  areNestedValuesEqual(prev.region, next.region) &&
  areNestedValuesEqual(prev.order, next.order) &&
  areNestedValuesEqual(prev.travelDetails, next.travelDetails);

const areDriverLiveLocationsEqual = (
  prev?: DriverMarker["liveLocation"],
  next?: DriverMarker["liveLocation"],
) => prev?.latitude === next?.latitude && prev?.longitude === next?.longitude;

const areDriverMarkersEqual = (prev: DriverMarker, next: DriverMarker) =>
  prev.driverId === next.driverId &&
  prev.color === next.color &&
  prev.fullName === next.fullName &&
  prev.startTime === next.startTime &&
  prev.actualStartTime === next.actualStartTime &&
  prev.estimatedTimeOfArrival === next.estimatedTimeOfArrival &&
  prev.disconnected === next.disconnected &&
  areDriverLiveLocationsEqual(prev.liveLocation, next.liveLocation) &&
  areNestedValuesEqual(prev.startingPoint, next.startingPoint);

const useRoutePlannerStore = create<RoutePlannerStore & RoutePlannerActions>(
  (setState, getState) => ({
    ...initialState,
    setMapHeight: (height) => setState({ mapHeight: height }),
    setRoutes: (data) =>
      setState(() => {
        const minAndMax = getMinAndMaxTime(data);

        return {
          routes: data,
          minAndMaxStartTimeOfRoutes: minAndMax,
        };
      }),
    setAddresses: (data: MapMarker[]) =>
      setState((state) => {
        // Preserve selection state by checking if any new addresses are in the selectedAddresses
        const selectedIds = new Set(
          state.selectedAddresses.map((addr) => addr.id),
        );
        const previousAddressesById = new Map(
          state.addresses.map((address) => [address.id, address]),
        );
        const incomingAddressesById = new Map(
          data.map((address) => [address.id, address]),
        );
        let addressesChanged = state.addresses.length !== data.length;

        const addressesWithSelectionState = data.map((address, index) => {
          const nextAddress = {
            ...address,
            selected: selectedIds.has(address.id),
          };
          const previousAddress = previousAddressesById.get(address.id);
          const stableAddress =
            previousAddress && areMapMarkersEqual(previousAddress, nextAddress)
              ? previousAddress
              : nextAddress;

          if (stableAddress !== state.addresses[index]) {
            addressesChanged = true;
          }

          return stableAddress;
        });

        // Update selectedAddresses with fresh data while preserving selection
        let selectedAddressesChanged = false;
        const updatedSelectedAddresses = state.selectedAddresses.map(
          (selectedAddr) => {
            const freshData = incomingAddressesById.get(selectedAddr.id);
            if (freshData) {
              const nextSelectedAddress = {
                ...freshData,
                selected: true,
              };
              const stableSelectedAddress = areMapMarkersEqual(
                selectedAddr,
                nextSelectedAddress,
              )
                ? selectedAddr
                : nextSelectedAddress;

              if (stableSelectedAddress !== selectedAddr) {
                selectedAddressesChanged = true;
              }

              return stableSelectedAddress;
            }
            return selectedAddr;
          },
        );

        if (!addressesChanged && !selectedAddressesChanged) {
          return state;
        }

        return {
          addresses: addressesWithSelectionState,
          selectedAddresses: updatedSelectedAddresses,
        };
      }),
    setSidebar: (val) => setState(() => ({ sidebar: val })),
    setRouteLocked: (val) => setState(() => ({ routeLocked: val })),
    setVirtualRoute: (val) => setState(() => ({ virtualRoute: val })),
    toggleDrawing: (val, mode = LassoMode.REPLACE) =>
      setState(() => ({ drawing: val, lassoMode: mode })),
    setMapZoom: (val) => setState(() => ({ mapZoom: val })),
    // No-op when unchanged to avoid a redundant update loop from [date].tsx.
    setDate: (val) =>
      setState((state) => (state.date === val ? state : { date: val })),
    setQuery: (val) => setState(() => ({ query: val })),
    findAddress: (id) => getState().addresses.find((item) => item.id === id),

    setPendingDriverUpdate: (val) =>
      setState(() => ({ pendingDriverUpdate: val })),
    setPendingNotificationUpdate: (val) =>
      setState(() => ({ pendingNotificationUpdate: val })),
    setSelectedAddressesForSequencing: (addresses: MapMarker[], lastSequence) =>
      setState(() => ({
        selectedAddressesForSequencing: addresses,
        lastSequence,
        sidebar: addresses.length > 0,
        sequencedAddresses: [],
        remainingSequences: addresses.length,
        allSequenced: false,
      })),
    updateSelectedAddresses: (selectedAddresses) =>
      setState(() => ({ selectedAddresses })),
    setSelectedAddress: (selected, multiSelect = false) =>
      setState((state) => {
        const { addresses = [], selectedAddresses = [] } = state;

        let selectedArray: MapMarker[];

        if (multiSelect) {
          // Existing multi-select logic
          const newSelection = Array.isArray(selected) ? selected : [selected];
          // Use Set for O(1) lookups instead of array operations
          const existingIds = new Set(selectedAddresses.map((addr) => addr.id));

          selectedArray = [...selectedAddresses];
          newSelection.forEach((item) => {
            if (existingIds.has(item.id)) {
              // Remove if already selected
              selectedArray = selectedArray.filter(
                (addr) => addr.id !== item.id,
              );
              existingIds.delete(item.id);
            } else {
              // Add if not selected
              selectedArray.push(item);
              existingIds.add(item.id);
            }
          });
        } else {
          // Single-select logic: only keep the clicked item
          const clickedItem = Array.isArray(selected) ? selected[0] : selected;
          const isAlreadySelected = selectedAddresses.some(
            (addr) => addr.id === clickedItem.id,
          );
          selectedArray = isAlreadySelected ? [] : [clickedItem];
        }

        // Create a Set of selected IDs for O(1) lookups instead of O(n) for each address
        const selectedIdsSet = new Set(selectedArray.map((addr) => addr.id));

        // Only update addresses that actually changed selection state
        // Skip the full map if no addresses are selected and none were selected before
        const prevSelectedIds = new Set(
          selectedAddresses.map((addr) => addr.id),
        );
        const hasSelectionChanged =
          selectedIdsSet.size !== prevSelectedIds.size ||
          [...selectedIdsSet].some((id) => !prevSelectedIds.has(id)) ||
          [...prevSelectedIds].some((id) => !selectedIdsSet.has(id));

        // Only create new addresses array if selection actually changed
        const updatedAddresses = hasSelectionChanged
          ? addresses.map((address) => {
              const shouldBeSelected = selectedIdsSet.has(address.id);
              // Only create new object if selected state changed
              if (address.selected !== shouldBeSelected) {
                return { ...address, selected: shouldBeSelected };
              }
              return address;
            })
          : addresses;

        const sameRegionId =
          (!!selectedArray[0]?.region?.id || !!selectedArray[0]?.routeId) &&
          selectedArray.every(
            (address) =>
              address.region?.id === selectedArray[0].region?.id ||
              address.routeId === selectedArray[0].routeId,
          );

        const sortedAddresses = selectedArray
          .filter((address) => address.locationSequence !== null)
          .sort((a, b) => a.locationSequence! - b.locationSequence!);

        const isValidSequence = sameRegionId
          ? sortedAddresses.every(
              (address, index, arr) =>
                index === 0 ||
                address.locationSequence ===
                  arr[index - 1].locationSequence! + 1,
            )
          : false;

        // Reset sequencing state when selection changes significantly
        const shouldResetSequencing =
          selectedArray.length === 0 ||
          selectedArray.length !== state.selectedAddresses.length ||
          !selectedArray.every((addr) => prevSelectedIds.has(addr.id));

        return {
          selectedAddresses: selectedArray,
          addresses: updatedAddresses,
          validSequence: isValidSequence,
          sidebar: selectedArray.length > 0,
          sameRegionId,
          shouldExcludeLocation: hasInProgressLocations(sortedAddresses),
          // Reset sequencing when selection changes
          ...(shouldResetSequencing && {
            sequencedAddresses: [],
            remainingSequences: selectedArray.length,
          }),
        };
      }),
    setMapCentering: (id, addresses) =>
      setState((state) => {
        let routeAddresses: MapMarker[] = [];
        if (addresses) {
          routeAddresses = addresses;
        }
        if (id) {
          routeAddresses = state.addresses.filter(
            (address) => address.region?.id === id || address.routeId === id,
          );
        }
        return {
          mapCentering: routeAddresses,
        };
      }),
    clearMapCentering: () => setState({ mapCentering: [] }),
    setDriverMarkers: (driverMarkers) =>
      setState((state) => {
        const previousMarkersById = new Map(
          state.driverMarkers.map((marker) => [marker.driverId, marker]),
        );
        let driverMarkersChanged =
          state.driverMarkers.length !== driverMarkers.length;

        const stableDriverMarkers = driverMarkers.map((driverMarker, index) => {
          const previousMarker = previousMarkersById.get(driverMarker.driverId);
          const stableMarker =
            previousMarker &&
            areDriverMarkersEqual(previousMarker, driverMarker)
              ? previousMarker
              : driverMarker;

          if (stableMarker !== state.driverMarkers[index]) {
            driverMarkersChanged = true;
          }

          return stableMarker;
        });

        if (!driverMarkersChanged) {
          return state;
        }

        return { driverMarkers: stableDriverMarkers };
      }),
    updateDriverMarkerLiveLocation: (driverId, liveLocation) =>
      setState((state) => {
        if (!driverId) return state;

        let driverMarkerChanged = false;
        const driverMarkers = state.driverMarkers.map((driverMarker) => {
          if (driverMarker.driverId !== driverId) return driverMarker;

          if (
            areDriverLiveLocationsEqual(driverMarker.liveLocation, liveLocation)
          ) {
            return driverMarker;
          }

          driverMarkerChanged = true;
          return {
            ...driverMarker,
            liveLocation,
          };
        });

        if (!driverMarkerChanged) {
          return state;
        }

        return { driverMarkers };
      }),
    setDriverMapCentering: (startingPoint) =>
      setState(() => ({ driverMapCentering: startingPoint })),
    updateAddressStatus: (id, newStatus: AddressStatus) =>
      setState((state) => {
        const { addresses, routes } = state;

        // Update addresses
        const updatedAddresses = addresses.map((address) =>
          address.id === id ? { ...address, status: newStatus } : address,
        );

        // Also need to update and recategorize routes
        const updatedRoutes = routes.map((route) => {
          const hasLocation = route.locations?.some(
            (loc) => loc.locationId === id,
          );
          if (!hasLocation) return route;

          const updatedLocations = route.locations?.map((loc) =>
            loc.locationId === id
              ? {
                  ...loc,
                  order: {
                    ...loc.order,
                    status: newStatus,
                    customerSkipOverridden:
                      loc.order?.customerSkipOverridden ?? false,
                  },
                }
              : loc,
          );

          return {
            ...route,
            locations: updatedLocations,
          };
        });

        return {
          addresses: updatedAddresses,
          routes: updatedRoutes,
        };
      }),
    removeSelected: (id: string) =>
      setState((state) => {
        const { selectedAddresses, addresses } = state;
        const updatedSelectedAddresses =
          selectedAddresses?.filter((address) => address.id !== id) || [];

        if (updatedSelectedAddresses.length === 0) {
          setState({ sidebar: false });
        }

        // Use Set for O(1) lookups
        const selectedIdsSet = new Set(
          updatedSelectedAddresses.map((addr) => addr.id),
        );

        const updatedAddresses =
          addresses &&
          addresses.map((address: MapMarker) => {
            const shouldBeSelected = selectedIdsSet.has(address.id);
            // Only create new object if selected state changed
            if (address.selected !== shouldBeSelected) {
              return { ...address, selected: shouldBeSelected };
            }
            return address;
          });

        // Recompute validity flags from the remaining selection, the same way
        // setSelectedAddress/setDirectSelection do. Without this, unchecking
        // an off-route address from the sidebar left sameRegionId/validSequence
        // stuck at their stale (invalid) values (ESR-1627).
        const sameRegionId =
          (!!updatedSelectedAddresses[0]?.region?.id ||
            !!updatedSelectedAddresses[0]?.routeId) &&
          updatedSelectedAddresses.every(
            (address) =>
              address.region?.id === updatedSelectedAddresses[0].region?.id ||
              address.routeId === updatedSelectedAddresses[0].routeId,
          );

        const sortedAddresses = updatedSelectedAddresses
          .filter((address) => address.locationSequence !== null)
          .sort((a, b) => a.locationSequence! - b.locationSequence!);

        const isValidSequence = sameRegionId
          ? sortedAddresses.every(
              (address, index, arr) =>
                index === 0 ||
                address.locationSequence ===
                  arr[index - 1].locationSequence! + 1,
            )
          : false;

        return {
          selectedAddresses: updatedSelectedAddresses,
          addresses: updatedAddresses,
          validSequence: isValidSequence,
          sameRegionId,
          shouldExcludeLocation: hasInProgressLocations(sortedAddresses),
        };
      }),
    setDirectSelection: (addresses: MapMarker[]) =>
      setState((state) => {
        // Use Set for O(1) lookups
        const selectedIdsSet = new Set(addresses.map((addr) => addr.id));

        const updatedAddresses = state.addresses.map((address) => {
          const shouldBeSelected = selectedIdsSet.has(address.id);
          // Only create new object if selected state changed
          if (address.selected !== shouldBeSelected) {
            return { ...address, selected: shouldBeSelected };
          }
          return address;
        });

        const sameRegionId =
          (!!addresses[0]?.region?.id || !!addresses[0]?.routeId) &&
          addresses.every(
            (address) =>
              address.region?.id === addresses[0].region?.id ||
              address.routeId === addresses[0].routeId,
          );

        const sortedAddresses = addresses
          .filter((address) => address.locationSequence !== null)
          .sort((a, b) => a.locationSequence! - b.locationSequence!);

        const isValidSequence = sameRegionId
          ? sortedAddresses.every(
              (address, index, arr) =>
                index === 0 ||
                address.locationSequence ===
                  arr[index - 1].locationSequence! + 1,
            )
          : false;

        return {
          selectedAddresses: addresses,
          addresses: updatedAddresses,
          validSequence: isValidSequence,
          sidebar: addresses.length > 0,
          sameRegionId,
          shouldExcludeLocation: hasInProgressLocations(sortedAddresses),
        };
      }),
    resetAllSelected: () =>
      setState((state) => {
        const { addresses } = state;

        let addressesChanged = false;
        const allAddresses = addresses.map((address: MapMarker) => {
          if (!address.selected) return address;

          addressesChanged = true;
          return {
            ...address,
            selected: false,
          };
        });

        return {
          addresses: addressesChanged ? allAddresses : addresses,
          selectedAddresses: [],
          sidebar: false,
          mapCentering: [],
          validSequence: false,
          sameRegionId: false,
          shouldExcludeLocation: false,
        };
      }),
    setMoveDialog: (moveDialogValues) =>
      setState(() => ({ moveDialog: moveDialogValues })),
    setAllSequenced: (val) => setState(() => ({ allSequenced: val })),
    setMoveValues: (values: MoveValues) =>
      setState(() => ({ moveValues: values })),
    setMoveDialogType: (val: "moveToRoute" | "changeStatus" | "") =>
      setState(() => ({ moveDialogType: val })),
    setMoveOrders: (orders: MoveOrders) =>
      setState(() => ({ moveOrders: orders })),
    setMoveOrdersDialogOpen: (val: boolean) =>
      setState(() => ({ moveOrdersDialogOpen: val })),
    setNewVirtualRoutePriority: (val: number) =>
      setState(() => ({ newVirtualRoutePriority: val })),
    setPendingVirtualRouteDelete: (val: PendingVirtualRouteDeleteValues) =>
      setState(() => ({ pendingVirtualRouteDelete: val })),
    setPendingUnassignedMove: (val?: PendingUnassignedMoveValues) =>
      setState(() => ({ pendingUnassignedMove: val })),
    setIsLoadingModalShown: (show: boolean) =>
      setState(() => ({ isLoadingModalShown: show })),
    setTransitioningRoutesPhase: (
      transitioningRoutesPhase?: TransitioningRoutePhase,
    ) => setState(() => ({ transitioningRoutesPhase })),
    setActiveModal: (activeModal: Modals | null) =>
      setState(() => ({ activeModal })),
    setHasLiveStateChanges: (hasLiveStateChanges) =>
      setState(() => ({ hasLiveStateChanges })),
    setHasPlanningChanges: (hasPlanningChanges) =>
      setState(() => ({ hasPlanningChanges })),
    setSearchedAddressForCustomer: (address: string) =>
      setState(() => ({ searchedAddressForCustomer: address })),
    setIsCalculatingEstTime: (isCalculatingEstTime: boolean) =>
      setState(() => ({ isCalculatingEstTime })),
    setIsCommittingLiveRoutes: (isCommittingLiveRoutes: boolean) =>
      setState(() => ({ isCommittingLiveRoutes })),
    setInitialLiveEstSettlementDate: (date?: string) =>
      setState(() => ({
        initialLiveEstSettlementDate: date,
        initialLiveEstSettlementClearedAt: date ? undefined : Date.now(),
      })),
    setDriverStatusSettlementDate: (date?: string) =>
      setState(() => ({
        driverStatusSettlementDate: date,
        driverStatusSettlementStartedAt: date ? Date.now() : undefined,
        driverStatusSettlementClearedAt: date ? undefined : Date.now(),
      })),
    openAddCustomerModal: (searchedAddress: string) =>
      setState(() => ({
        searchedAddressForCustomer: searchedAddress,
        activeModal: Modals.ADD_CUSTOMER,
      })),
    closeAddCustomerModal: () =>
      setState(() => ({
        searchedAddressForCustomer: "",
        activeModal: null,
      })),
    addPendingEstCalculation: (routeId: string) =>
      setState((state) => {
        const newSet = new Set(state.pendingEstCalculationRouteIds);
        newSet.add(routeId);
        return { pendingEstCalculationRouteIds: newSet };
      }),
    removePendingEstCalculation: (routeId: string) =>
      setState((state) => {
        const newSet = new Set(state.pendingEstCalculationRouteIds);
        newSet.delete(routeId);
        return { pendingEstCalculationRouteIds: newSet };
      }),
    clearPendingEstCalculations: () =>
      setState(() => ({ pendingEstCalculationRouteIds: new Set<string>() })),
    setSyncedCurrentTime: (time: DateTime) =>
      setState(() => ({ syncedCurrentTime: time })),
    setAddressSequence: (addressId, lastSequence = 1, sequencingUnassigned) =>
      setState((state) => {
        const { selectedAddressesForSequencing, selectedAddresses } = state;
        let assignedSequence: number = lastSequence || 0;
        if (lastSequence === undefined || lastSequence === 0) {
          assignedSequence = 1;
        }

        const selectedAddress = selectedAddressesForSequencing.find(
          (address) => address.id === addressId,
        );

        // If address is already sequenced, no action should be taken
        // But allow sequencing for unassigned addresses in the move flow
        if (
          selectedAddress?.region?.id &&
          selectedAddress.region.id !== "unassigned"
        )
          return state;

        const addressToRemove = selectedAddress;
        const sequenceToRemove =
          addressToRemove && addressToRemove.locationSequence;

        let updatedSelectedAddressesForSequencing: MapMarker[] = [];
        let nextSequence = assignedSequence + 1;

        if (sequenceToRemove === undefined || sequenceToRemove === null) {
          // Adding sequence to an address
          updatedSelectedAddressesForSequencing =
            selectedAddressesForSequencing.map((address) => {
              if (
                address.id === addressId &&
                address.locationSequence === null
              ) {
                return { ...address, locationSequence: assignedSequence };
              }
              return address;
            });

          // For sequencingUnassigned flow, we need to work with selectedAddressesForSequencing
          // Calculate the next sequence properly
          const sequences = [
            ...updatedSelectedAddressesForSequencing
              .map((address) => address.locationSequence)
              .filter(
                (seq): seq is number => seq !== undefined && seq !== null,
              ),
          ];

          const highestSequence = Math.max(
            0,
            ...(sequences.length ? sequences : [assignedSequence - 1]),
          );
          nextSequence = sequences.length
            ? highestSequence + 1
            : assignedSequence + 1;
        } else {
          // Removing sequence from an address
          updatedSelectedAddressesForSequencing =
            selectedAddressesForSequencing.map((address) => {
              if (address.id === addressId) {
                return { ...address, locationSequence: null };
              }
              if (
                address.locationSequence &&
                address.locationSequence > sequenceToRemove &&
                selectedAddresses.some(
                  (addressesToBeSequenced) =>
                    addressesToBeSequenced.id === address.id,
                )
              ) {
                return {
                  ...address,
                  locationSequence: address.locationSequence - 1,
                };
              }
              return address;
            });

          const sequences = [
            ...updatedSelectedAddressesForSequencing
              .filter(
                (item) =>
                  item.region === null || item.region?.id === "unassigned",
              )
              .map((address) => address.locationSequence)
              .filter(
                (seq): seq is number => seq !== undefined && seq !== null,
              ),
          ];

          const highestSequence = Math.max(
            0,
            ...(sequences.length ? sequences : [assignedSequence - 1]),
          );

          nextSequence = sequences.length
            ? highestSequence + 1
            : highestSequence;
        }

        const allAddressesToBeSequenced =
          updatedSelectedAddressesForSequencing.filter(
            (address) =>
              !address.region?.id || address.region?.id === "unassigned",
          );

        const allSequencedAddresses = sequencingUnassigned
          ? updatedSelectedAddressesForSequencing.every(
              (address) => address.locationSequence !== null,
            )
          : allAddressesToBeSequenced.every(
              (addressToBeSeq) => addressToBeSeq.locationSequence !== null,
            );

        // Update sequencedAddresses with addresses that have sequences
        const sequencedAddresses = updatedSelectedAddressesForSequencing
          .filter((item) => item.locationSequence !== null)
          .sort(
            (a, b) =>
              (a.locationSequence as number) - (b.locationSequence as number),
          );

        // Calculate remaining sequences based on selectedAddressesForSequencing for sequencing flow
        const totalAddressesToSequence = sequencingUnassigned
          ? selectedAddressesForSequencing.length
          : selectedAddresses.length;
        const remainingSequences =
          totalAddressesToSequence - sequencedAddresses.length;

        // Also update selectedAddresses to keep them in sync with selectedAddressesForSequencing
        const updatedSelectedAddresses = selectedAddresses.map((address) => {
          const correspondingAddress =
            updatedSelectedAddressesForSequencing.find(
              (addr) => addr.id === address.id,
            );
          return correspondingAddress || address;
        });

        return {
          lastSequence: nextSequence > 1 ? nextSequence : 1,
          selectedAddresses: updatedSelectedAddresses,
          selectedAddressesForSequencing: updatedSelectedAddressesForSequencing,
          sequencedAddresses: sequencedAddresses,
          remainingSequences: remainingSequences,
          allSequenced: allSequencedAddresses,
        };
      }),
    setSequencedAddresses: (addresses: MapMarker[]) =>
      setState({ sequencedAddresses: addresses }),
    moveItem: (dragIndex: number, hoverIndex: number) =>
      setState((state) => {
        const {
          sequencedAddresses,
          selectedAddresses,
          selectedAddressesForSequencing,
        } = state;

        // Work with sequencedAddresses for both flows (same as region management)
        const items = [...sequencedAddresses];
        const [draggedItem] = items.splice(dragIndex, 1);
        items.splice(hoverIndex, 0, draggedItem);

        // Find the starting sequence number (preserve the original sequence range)
        const originalSequences = sequencedAddresses
          .map((item) => item.locationSequence)
          .filter((seq): seq is number => seq !== null && seq !== undefined)
          .sort((a, b) => a - b);

        const startingSequence =
          originalSequences.length > 0 ? originalSequences[0] : 1;

        // Update the locationSequence based on the new order, maintaining the original sequence range
        const updatedItems = items.map((item, index) => ({
          ...item,
          locationSequence: startingSequence + index,
        }));

        // Update selectedAddresses to reflect the new sequences
        const updatedSelectedAddresses = selectedAddresses?.map((address) => {
          const foundItem = updatedItems.find((item) => item.id === address.id);
          if (foundItem && address.locationSequence !== null) {
            return {
              ...address,
              locationSequence: foundItem.locationSequence,
            };
          }
          return address;
        });

        // Also update selectedAddressesForSequencing to reflect the new sequences
        const updatedSelectedAddressesForSequencing =
          selectedAddressesForSequencing?.map((address) => {
            const foundItem = updatedItems.find(
              (item) => item.id === address.id,
            );
            if (foundItem && address.locationSequence !== null) {
              return {
                ...address,
                locationSequence: foundItem.locationSequence,
              };
            }
            return address;
          });

        // Check if all addresses are sequenced
        const allSequenced =
          updatedItems.length === selectedAddresses.length &&
          updatedItems.every((item) => item.locationSequence !== null);

        return {
          sequencedAddresses: updatedItems,
          selectedAddresses: updatedSelectedAddresses,
          selectedAddressesForSequencing: updatedSelectedAddressesForSequencing,
          allSequenced,
          remainingSequences: selectedAddresses.length - updatedItems.length,
        };
      }),
  }),
);

export default useRoutePlannerStore;
